import { useLoading } from '@psl/components/LoadingIndicator/LoadingIndicator';
import { useAlert } from '@psl/functions/ux/useAlert';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { usePersistFn } from './usePersistFn';

export class CancelError extends Error {}

export function useOnPromise<I extends any[], O>(
  action: (...input: I) => O | Promise<O>,
  {
    spinner,
    onError,
  }: {
    onError?: (error: Error) => void;
    spinner?: boolean;
  } = {},
) {
  const alert = useAlert();
  const loader = useLoading();

  const [state, setState] = useState<{ loading: boolean; error?: Error }>({
    loading: false,
  });

  const onPromise = usePersistFn(async (...i: I) => {
    try {
      if (spinner) {
        loader.show();
      }
      setState({ loading: true, error: undefined });
      const result = await action(...i);
      setState({ loading: false });
      return result;
    } catch (e) {
      if (e instanceof CancelError) {
        setState({ loading: false });
      } else {
        const msg =
          e instanceof AxiosError
            ? e.response?.data.message
            : (e as any).message;
        if (onError) {
          onError(e as Error);
        } else {
          console.error(e);
          void alert({
            title: 'Oops!',
            message: msg,
          });
        }
        setState({ loading: false, error: e as Error });
      }
    } finally {
      if (spinner) {
        loader.hide();
      }
    }
  });
  return { ...state, onPromise };
}
